import {
    Alert,
    Anchor,
    Badge,
    Code,
    Group,
    Paper,
    Stack,
    Table,
    Text,
} from '@mantine/core';

import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';
import {
    DefectSignalBadge,
    DefectStatusBadge,
} from './Badges.tsx';
import {
    classifyDefect,
    DefectKind,
} from '../utils/classifyDefect.ts';
import {DefectSignature} from '../types';

export const Profile = ({record}: { record: DefectSignature }) => {

    const kind = classifyDefect(record);

    const rows: { label: string, value: React.ReactNode }[] = [
        {
            label: 'Action',
            value: <Code>{record.actionCode}</Code>,
        },
        {
            label: 'Signal',
            value: <DefectSignalBadge record={record} kind={kind}/>,
        },
        {
            label: 'Triage state',
            value: <DefectStatusBadge status={record.status}/>,
        },
        {
            label: 'Occurrences',
            value: <Text size={'sm'}>{record.occurrenceCount}</Text>,
        },
        {
            label: 'Distinct users affected',
            value: (
                <Group gap={'xs'}>
                    <Text size={'sm'}>{record.distinctUserCount}</Text>
                    {record.distinctUserCount === 1 && record.occurrenceCount > 3 && (
                        // Worth saying out loud: a defect one user hits repeatedly
                        // is a different investigation from one that is spreading.
                        <Badge size={'xs'} variant={'light'} color={'gray'}>
                            single user
                        </Badge>
                    )}
                </Group>
            ),
        },
        {
            label: 'First seen',
            value: <Text size={'sm'}>{formatTimestamp(record.firstSeenAt)}</Text>,
        },
        {
            label: 'Last seen',
            value: <Text size={'sm'}>{formatTimestamp(record.lastSeenAt)}</Text>,
        },
        {
            label: 'Ticket',
            value: record.jiraKey
                ? (
                    <Anchor
                        href={`https://flexinetwork.atlassian.net/browse/${record.jiraKey}`}
                        target={'_blank'}
                        rel={'noreferrer'}
                        size={'sm'}
                        ff={'monospace'}
                    >
                        {record.jiraKey}
                    </Anchor>
                )
                : <Text size={'sm'} c={'dimmed'}>Not filed</Text>,
        },
        {
            label: 'Fingerprint',
            value: <Text size={'xs'} ff={'monospace'} c={'dimmed'}>{record.id}</Text>,
        },
    ];

    return (
        <Stack gap={'md'}>
            {kind === DefectKind.REGRESSION && record.resolvedAt && (
                // The most important thing this page can say, so it says it first
                // and loudly rather than leaving a green "Resolved" badge to
                // quietly mislead.
                //
                // `resolvedAt` is re-checked here only to narrow the optional for
                // the compiler — isRegression already guarantees it is set, and a
                // non-null assertion would hide a real bug if that ever changed.
                <Alert color={'red'} title={'This defect came back'}>
                    It was marked resolved at {formatTimestamp(record.resolvedAt)} but has been
                    seen again since, most recently at {formatTimestamp(record.lastSeenAt)}.
                </Alert>
            )}

            <Paper withBorder p={'md'} radius={'md'}>
                <Table verticalSpacing={'sm'} withRowBorders={false}>
                    <Table.Tbody>
                        {rows.map((row) => (
                            <Table.Tr key={row.label}>
                                <Table.Td w={'40%'}>
                                    <Text size={'sm'} c={'dimmed'}>{row.label}</Text>
                                </Table.Td>
                                <Table.Td>{row.value}</Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Paper>

            <Paper withBorder p={'md'} radius={'md'}>
                <Stack gap={'xs'}>
                    <Text size={'sm'} fw={500}>Normalized error</Text>
                    <Text size={'xs'} c={'dimmed'}>
                        What these occurrences have in common. The placeholders are the parts
                        that vary per run — this is why they group as one defect.
                    </Text>
                    <Code block>{record.normalizedError ?? '(none recorded)'}</Code>
                </Stack>
            </Paper>

            <Paper withBorder p={'md'} radius={'md'}>
                <Stack gap={'xs'}>
                    <Text size={'sm'} fw={500}>Sample error</Text>
                    <Text size={'xs'} c={'dimmed'}>
                        One real occurrence, verbatim.
                    </Text>
                    <Code block>{record.sampleError ?? '(none recorded)'}</Code>
                </Stack>
            </Paper>
        </Stack>
    );
};
